import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from "react-redux";
import { getTradesPage, getUserTags, createTag, setTradeTags, getTradeTags } from '../store/trades/thunks';
import { getStudyNoteTickers } from '../store/study/thunks';
import { getTradeReview, createTradeReview, editTradeReview, uploadReviewScreenshot } from '../store/journal/thunks';
import { useNavigate, useLocation } from "react-router-dom";
import '../styles/summary.css';
import '../styles/logtrade.css';
import '../styles/filter.css';
import { returnInTZ } from '../utils/timezone';
import { formatEmotionalState } from '../utils/reviewLabels';
import axios from "axios";
import { IoFilter } from "react-icons/io5";
import { motion, AnimatePresence } from 'framer-motion';
import TradingSpinner from './TradingSpinner';
import SummarySkeleton from './SummarySkeleton';
import {
  Flex,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  TableContainer,
  Center,
  UnorderedList,
  ListItem,
  Heading,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Spacer,
  Input,
  useColorMode,
  Button,
  ButtonGroup,
  InputGroup,
  Stack,
  Link,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  FormControl,
  FormLabel,
  HStack,
  VStack,
  Checkbox,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Tooltip,
  Icon,
  Image
} from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import { IoInformationCircleOutline } from "react-icons/io5";
import { update, getTrade, reset, deleteTrade, searchTicker, importCsv, exportCsv, updateTrades, bulkUpdateCsv } from '../store/trade';
import { fixCsvImport } from '../store/ai/thunks';
import { resolveCsvFixRejection } from '../store/ai/csvFixErrors';


import useToastNotification from "../hooks/useToastNotification";
import useAsyncState from "../hooks/useAsyncState";
import usePagination from "../hooks/usePagination";
import TradePagination from "./summary/TradePagination";
import AccountValueSyncToggle from "./common/AccountValueSyncToggle";
import CsvFixResultsDialog from "./summary/CsvFixResultsDialog";
import TradeFilters from "./summary/TradeFilters";
import TradeTable from "./summary/TradeTable";
import BulkUpdateCsvView from "./summary/BulkUpdateCsvView";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function Summary({ user, trades }) {
  const btnRef = React.useRef()
  const navigate = useNavigate();
  const showToast = useToastNotification();
  const dispatch = useDispatch();
  const query = useQuery();
  const { trade } = useSelector((state) => state.trade);
  const { success } = useSelector((state) => state.trade);
  //const { trades } = useSelector((state) => state.auth);
  const tradeError = useSelector((state) => state.trade.error);
  const tradeInfo = useSelector((state) => state.trade.info);
  useAsyncState({
    onError: (info) => showToast(info?.response?.data?.result, { status: "error" }),
  });
  const hasTrades = ((trades && trades.trades && Object.keys(trades.trades).length > 0) ? (true):(false));
  const hasTrade = ((trade && Object.keys(trade).length > 2) ? (true):(false));

  // Same pattern as Home/PnlCalendar: state.ui.loading flips false on first
  // concurrent thunk resolution, but the empty-state TradeTable shouldn't
  // render until getTradesPage has actually populated state.trades.
  const tradesLoaded = trades && Object.keys(trades).length > 0;

  const [editTrade, setEditTrade] = useState(false);
  const [bulkUpdateWithCSV, setBulkUpdateWithCSV] = useState(false);
  const [toggleFilter, setToggleFilter] = useState(false);

  const [visib, setVisib] = useState(false);
  function checkVisbility(tradePayload){
    setVisib(tradePayload.security_type === "Options");
  }

  const [filters, setFilters] = useState({});

  const handleFilterChange = (newFilter) => {
    query.set('filter', newFilter);
    navigate(`?${query.toString()}`);
  };

  function filtersToQueryString(filters) {
    const params = new URLSearchParams();
    for (const key in filters) {
      if (filters[key] !== '') {
        params.append(key, filters[key]);
      }
    }
    return params.toString();
  }

  const filterLabelMap = {
    trade_type: 'Trade Type',
    security_type: 'Security Type',
    trade_direction: 'Direction',
    ticker_name: 'Ticker',
    from_date: 'From',
    to_date: 'To',
    tag_id: 'Tag',
  };

  const keysToSkip = ['page', 'numrows'];

  const handleRemoveFilter = async (key) => {
    const updated = { ...filters };
    delete updated[key];
    if (key === 'from_date' || key === 'to_date') {
      delete updated['from_date'];
      delete updated['to_date'];
      setFilterFromDate('');
      setFilterToDate('');
    }
    if (key === 'trade_type') setFilterTradeType('');
    if (key === 'security_type') setFilterSecurityType('');
    if (key === 'trade_direction') setFilterTradeDirection('');
    if (key === 'ticker_name') { setFilterTickerName(''); setSearchTickerValue(''); setSelectedTickerValue(''); }
    if (key === 'tag_id') setFilterTag('');

    if (Object.keys(updated).filter(k => !keysToSkip.includes(k)).length === 0) {
      window.localStorage.removeItem('SummaryFilters');
      const paginationFilters = { page: 1, numrows: num_results };
      await dispatch(getTradesPage({ filters: paginationFilters }));
      handleFilterChange(filtersToQueryString({}));
      setFilters({});
    } else {
      window.localStorage.setItem('SummaryFilters', JSON.stringify(updated));
      const paginationFilters = { ...updated, page: 1, numrows: num_results };
      await dispatch(getTradesPage({ filters: paginationFilters }));
      handleFilterChange(filtersToQueryString(updated));
      setFilters(updated);
    }
    setSelectedRow([]);
  };

  useEffect(() => {
    const savedSummaryFilters = window.localStorage.getItem('SummaryFilters');
    if (savedSummaryFilters) {
      const SummaryFilters = JSON.parse(savedSummaryFilters);
      setFilterTradeType(SummaryFilters.trade_type || "");
      setFilterSecurityType(SummaryFilters.security_type || "");
      setFilterTradeDirection(SummaryFilters.trade_direction || "");
      setFilterTickerName(SummaryFilters.ticker_name || "");
      setSelectedTickerValue(SummaryFilters.ticker_name || "");
      setFilterFromDate(SummaryFilters.from_date || "");
      setFilterToDate(SummaryFilters.to_date || "");
      setFilterTag(SummaryFilters.tag_id || "");
      setFilters(SummaryFilters);
    }else{
      setFilterTradeType("");
      setFilterSecurityType("");
      setFilterTradeDirection("");
      setFilterTickerName("");
      setSelectedTickerValue("");
      setSearchTickerValue("");
      setFilterFromDate("");
      setFilterToDate("");
      setFilterTag("");
      setFilters({});
    }
  }, [trades]); // Empty dependency array means this runs once on mount

  useEffect(() => {
    dispatch(getUserTags());
  }, [dispatch]);

  // Gate the note-tickers fetch behind the slice's loaded flag — same loop
  // protection HomeWatchlistCard uses for the watchlist endpoint.
  const noteTickersLoaded = useSelector((state) => Boolean(state.study?.noteTickersLoaded));
  useEffect(() => {
    if (!noteTickersLoaded) {
      dispatch(getStudyNoteTickers());
    }
  }, [dispatch, noteTickersLoaded]);


  const pg = usePagination(trades, { defaultPageSize: 100 });
  const {
    page,
    totalCount,
    numResults: num_results,
    setNumResults,
    startOffset: pageStartOffset,
    endOffset: pageEndOffset,
    canGoPrev: backPageEnable,
    canGoNext: nextPageEnable,
  } = pg;

  useEffect(() => {
    if (hasTrade && 'rr' in trade && trade.rr != null) {
      setRisk(trade.rr.split(":")[0] || '1');
      setReward(trade.rr.split(":")[1] || '1');
    }
  }, [trade]); 

  const [trade_id, setTradeID] = useState(null);

  const [trade_type, setTradeType] = useState("");
  const [security_type, setSecurityType] = useState("");
  const [trade_direction, setTradeDirection] = useState("");
  const [ticker_name, setTickerName] = useState("");
  const [trade_date, setTradeDate] = useState("");
  const [expiry, setExpiry] = useState("");
  const [strike, setStrike] = useState("");
  const [buy_value, setBuyValue] = useState("");
  const [units, setUnits] = useState("");
  const [rr, setRR] = useState("");
  const [pnl, setPNL] = useState("");
  const [percent_wl, setPercentWL] = useState("");
  const [fees, setFees] = useState("");
  const [comments, setComments] = useState("");

  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#3182CE");
  const allTags = useSelector((state) => state.trades.tags) || [];

  const [reviewSetupType, setReviewSetupType] = useState("");
  const [reviewFollowedPlan, setReviewFollowedPlan] = useState("");
  const [reviewEmotionalState, setReviewEmotionalState] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewScreenshot, setReviewScreenshot] = useState(null);
  const [existingReviewId, setExistingReviewId] = useState(null);
  const [existingReview, setExistingReview] = useState(null);
  const [existingScreenshotUrl, setExistingScreenshotUrl] = useState(null);

  const [filter_trade_type, setFilterTradeType] = useState("");
  const [filter_security_type, setFilterSecurityType] = useState("");
  const [filter_trade_direction, setFilterTradeDirection] = useState("");
  const [filter_ticker_name, setFilterTickerName] = useState("");
  const [filter_from_date, setFilterFromDate] = useState("");
  const [filter_to_date, setFilterToDate] = useState("");
  const [filter_tag, setFilterTag] = useState("");


  const today = returnInTZ(new Date().toISOString());

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const [deletealertdialog, setDeleteAlertDialog] = useState(false);

  const authLoading = useSelector((state) => state.ui.loading);
  const tradeLoading = useSelector((state) => state.trade.loading);
  const aiCsvQueriesRemaining = useSelector((state) => state.ai.aiCsvQueriesRemaining);
  const isFreeUser = user && user.has_paid === 0;
  const csvQuotaExhausted = isFreeUser && aiCsvQueriesRemaining === 0;

  const [exportLoading, setExportLoading] = useState(false);

  const [editLoading, setEditLoading] = useState(false);

  const [, setEditButtonInstance] = useState(null);

  const [importCsvDialog, setImportDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvFixDialog, setCsvFixDialog] = useState(false);
  const [fixedCsvContent, setFixedCsvContent] = useState(null);
  const [editableCsvData, setEditableCsvData] = useState(null); // 2D array: [headers, ...rows]
  const [csvFixLoading, setCsvFixLoading] = useState(false);
  const [csvFixResult, setCsvFixResult] = useState(null);
  const [csvFixIsBulkUpdate, setCsvFixIsBulkUpdate] = useState(false);

  const resetCsvFixState = () => {
    setCsvFixDialog(false);
    setCsvFixResult(null);
    setFixedCsvContent(null);
    setEditableCsvData(null);
    setCsvFixIsBulkUpdate(false);
  };

  const [selectedRow, setSelectedRow] = useState([]);
  const [trade_ids, setSelectedTradeIds] = useState([]);

  const { colorMode } = useColorMode();

  const [searchValue, setSearchValue] = useState('');
  const [searchTickerValue, setSearchTickerValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchTickerResults, setSearchTickerResults] = useState([]);
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedTickerValue, setSelectedTickerValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [accountValueImportEnable, setAccountValueImportEnable] = useState(true);
  const [accountValueUpdateEnable, setAccountValueUpdateEnable] = useState(true);
  const [accountValueUpdateTradesEnable, setAccountValueUpdateTradesEnable] = useState(true);
  const [accountValueUpdateCSVEnable, setAccountValueUpdateCSVEnable] = useState(true);
  const [accountValueDeleteEnable, setAccountValueDeleteEnable] = useState(true);

  // Toggle the checkbox when the user clicks
  const handleCheckboxChange = (e,type) => {
    if (type === "Import"){
      setAccountValueImportEnable(e.target.checked);  // Update the state based on the toggle
    }
    if (type === "Update"){
      setAccountValueUpdateEnable(e.target.checked);  // Update the state based on the toggle
    }
    if (type === "UpdateTrades"){
      setAccountValueUpdateTradesEnable(e.target.checked);  // Update the state based on the toggle
    }
    if (type === "UpdateCSV"){
      setAccountValueUpdateCSVEnable(e.target.checked);  // Update the state based on the toggle
    }
    if (type === "Delete"){
      setAccountValueDeleteEnable(e.target.checked);  // Update the state based on the toggle
    }
  };

  const format = (val1,val2) => val1 + ":" + val2;
  const [risk, setRisk] = useState("1");
  const [reward, setReward] = useState("1");

  useEffect(() => {
    if(risk > 0 && reward > 0 && rr !== format(risk, reward)){
      setRR(format(risk,reward));
    }
  }, [risk, reward]); 

  useEffect(() => {
    if (rr && rr !== format(risk, reward)) {
      const [riskValue, rewardValue] = rr.split(':').map(val => parseInt(val));
      setRisk(riskValue);
      setReward(rewardValue);
    }
  }, [rr]);

  useEffect(() => {
    const fetchSearchResults = async () => {
      setIsLoading(true);
      const filter = {};
      if(searchTickerValue !== ''){
        filter.ticker_name = searchTickerValue;
      }
      const response = await dispatch(searchTicker({filter})); 
      const topResults = response.payload.tickers.map(f => [f.ticker_name]);
      setSearchTickerResults(topResults);
      setIsLoading(false);
    };

    if (searchTickerValue) {
      fetchSearchResults();
      setIsDropdownOpen(true);
    } else {
      setSearchTickerResults([]);
      setIsDropdownOpen(false);
    }
  }, [searchTickerValue]);

  useEffect(() => {
    evaluateSuccess();
  }, [success]); 

  const evaluateSuccess = async () => {
    if(success === true && trade && trade.result === "Trade Edited Successfully"){
      setEditTrade(false);
      showToast(trade.result);
      const filters = {};
      if(filter_trade_type !== ''){
        filters.trade_type = filter_trade_type;
      }
      if(filter_security_type !== ''){
        filters.security_type = filter_security_type;
      }
      if(filter_trade_direction !== ''){
        filters.trade_direction = filter_trade_direction;
      }
      if(filter_ticker_name !== ''){
        filters.ticker_name = filter_ticker_name;
      }
      if(filter_from_date !== '' || filter_to_date !== ''){
        if (filter_from_date !== '' && filter_to_date !== ''){
          filters.from_date = filter_from_date;
          filters.to_date = filter_to_date;
        }else if (filter_from_date !== '' && filter_to_date === ''){
          filters.from_date = filter_from_date;
          filters.to_date = today;
        }
        else if (filter_from_date === '' && filter_to_date !== ''){
          filters.from_date = "1900-01-01";
          filters.to_date = filter_to_date;
        }
      }
      filters.page = 1;
      filters.numrows = num_results;
      window.localStorage.setItem('SummaryFilters', JSON.stringify(filters)); 
      await dispatch(getTradesPage({ filters }));
      dispatch(
        reset()      
      );
      handleFilterChange(filtersToQueryString(filters));
      //window.localStorage.setItem('SummaryFilters', JSON.stringify(filters));
      clearFormStates();
      setSelectedRow([]);
      setTradeID(null);
      setSearchValue('');
      setIsDropdownOpen(false);
    }
    if(success === true && trade && trade.result && trade.result.includes("Trades Updated Successfully")){
      setEditTrade(false);
      showToast(trade.result);
      const filters = {};
      if(filter_trade_type !== ''){
        filters.trade_type = filter_trade_type;
      }
      if(filter_security_type !== ''){
        filters.security_type = filter_security_type;
      }
      if(filter_trade_direction !== ''){
        filters.trade_direction = filter_trade_direction;
      }
      if(filter_ticker_name !== ''){
        filters.ticker_name = filter_ticker_name;
      }
      if(filter_from_date !== '' || filter_to_date !== ''){
        if (filter_from_date !== '' && filter_to_date !== ''){
          filters.from_date = filter_from_date;
          filters.to_date = filter_to_date;
        }else if (filter_from_date !== '' && filter_to_date === ''){
          filters.from_date = filter_from_date;
          filters.to_date = today;
        }
        else if (filter_from_date === '' && filter_to_date !== ''){
          filters.from_date = "1900-01-01";
          filters.to_date = filter_to_date;
        }
      }
      filters.page = 1;
      filters.numrows = num_results;
      window.localStorage.setItem('SummaryFilters', JSON.stringify(filters)); 
      await dispatch(getTradesPage({ filters }));
      dispatch(
        reset()      
      );
      handleFilterChange(filtersToQueryString(filters));
      //window.localStorage.setItem('SummaryFilters', JSON.stringify(filters));
      clearFormStates();
      setSelectedRow([]);
      setTradeID(null);
      setSearchValue('');
      setIsDropdownOpen(false);
    }
    if(success === true && trade && trade.result === "Trade Successfully Deleted"){
      showToast(trade.result);
      dispatch(
        reset()      
      );
    }
    if(success === true && trade && trade.result === "Trades Successfully Deleted"){
      showToast(trade.result);
      dispatch(
        reset()      
      );
    }
    if(success === true && trade && trade.result === "Trades Imported Successfully"){
      showToast(trade.trades.length.toString() + " " + trade.result);
      dispatch(
        reset()      
      );
    }
    if(success === true && trade && trade.result && trade.result.includes("Trades Updates Imported Successfully")){
      let toast_message = trade.updated_ids.length.toString() + " " + trade.result;
      if(trade && trade.error_ids && trade.error_ids.length > 0){
        //toast_message += "\nSome Errors Present, Downloading Errors...";
        showToast("Some Errors Present, Downloading Errors...", { status: "error" });
      }
      showToast(toast_message);
      dispatch(
        reset()      
      );
    }
    if(success === true && trade && trade.result === "Trades Exported Successfully"){
      showToast(trade.result);
      dispatch(
        reset()      
      );
    }
  }

  useEffect(() => {
    evaluateError();
  }, [tradeError]);

  const evaluateError = () => {
    if(tradeError === true){
      if(tradeInfo.response.data.result === "Unable to Update Any Trades Successfully"){
        showToast(tradeInfo.response.data.result + ", Downloading Errors...", { status: "error" });
        if (tradeInfo.response.data.error_ids && tradeInfo.response.data.error_ids.length > 0) {
          downloadErrorCsv(tradeInfo.response.data.error_ids);
        }
      }else{
        showToast(tradeInfo.response.data.result, { status: "error" });
      }
    }
  }
  useEffect(() => {
    const savedUpdateTradeInfo = window.localStorage.getItem('updateTradeInfo');
    const savedUpdateBulkTradeInfo = window.localStorage.getItem('updateBulkTradeInfo');
    if (savedUpdateTradeInfo) {
      const updateTradeInfo = JSON.parse(savedUpdateTradeInfo);
      setTradeType(updateTradeInfo.trade_type || "");
      setSecurityType(updateTradeInfo.security_type || "");
      setTradeDirection(updateTradeInfo.trade_direction || "");
      setTickerName(updateTradeInfo.ticker_name || "");
      setTradeDate(updateTradeInfo.trade_date || "");
      setExpiry(updateTradeInfo.expiry || "");
      setStrike(updateTradeInfo.strike || "");
      setBuyValue(updateTradeInfo.buy_value || "");
      setUnits(updateTradeInfo.units || "");
      setRR(updateTradeInfo.rr || "");
      setPNL(updateTradeInfo.pnl || "");
      setPercentWL(updateTradeInfo.percent_wl || "");
      setFees(updateTradeInfo.fees || "");
      setComments(updateTradeInfo.comments || "");
      // Clear the saved info after loading it
      //window.localStorage.removeItem('userInfo');
    }
    if (savedUpdateBulkTradeInfo) {
      const updateBulkTradeInfo = JSON.parse(savedUpdateBulkTradeInfo);
      setTradeType(updateBulkTradeInfo.trade_type || "");
      setSecurityType(updateBulkTradeInfo.security_type || "");
      setTradeDirection(updateBulkTradeInfo.trade_direction || "");
      setTickerName(updateBulkTradeInfo.ticker_name || "");
      setTradeDate(updateBulkTradeInfo.trade_date || "");
      setExpiry(updateBulkTradeInfo.expiry || "");
      setStrike(updateBulkTradeInfo.strike || "");
      setBuyValue(updateBulkTradeInfo.buy_value || "");
      setUnits(updateBulkTradeInfo.units || "");
      setRR(updateBulkTradeInfo.rr || "");
      setPNL(updateBulkTradeInfo.pnl || "");
      setPercentWL(updateBulkTradeInfo.percent_wl || "");
      setFees(updateBulkTradeInfo.fees || "");
      setComments(updateBulkTradeInfo.comments || "");
      // Clear the saved info after loading it
      //window.localStorage.removeItem('userInfo');
    }
  }, []); // Empty dependency array means this runs once on mount

  function clearFormStates(resetExistingReview = true) {
    setTradeType("");
    setSecurityType("");
    setTradeDirection("");
    setTickerName("");
    setSelectedValue("");
    setSearchValue("");
    setTradeDate("");
    setExpiry("");
    setStrike("");
    setBuyValue("");
    setUnits("");
    setRR("");
    setPNL("");
    setPercentWL("");
    setFees("");
    setComments("");
    setRisk(hasTrade && 'rr' in trade && trade.rr != null ? trade.rr.split(":")[0] : "1");
    setReward(hasTrade && 'rr' in trade && trade.rr != null ? trade.rr.split(":")[1] : "1");
    window.localStorage.removeItem('updateTradeInfo');
    window.localStorage.removeItem('updateBulkTradeInfo');
    setReviewSetupType("");
    setReviewFollowedPlan("");
    setReviewEmotionalState("");
    setReviewNotes("");
    setReviewScreenshot(null);
    if (resetExistingReview) {
      setExistingReviewId(null);
      setExistingReview(null);
      setExistingScreenshotUrl(null);
    }
  }

  const changeShowOptions = (e) => {
    const choiceOptions = document.getElementById("optionsSelection");
    if (choiceOptions.value === "Shares"){
      setVisib(false);
      setExpiry("");
      setStrike("");
    }else if (choiceOptions.value === "Options"){
      setVisib(true);
      setExpiry("");
      setStrike("");
    }
    if (choiceOptions.value === "" && trade && trade.security_type && trade.security_type === "Options"){
      setVisib(true);
      setExpiry("");
      setStrike("");
    }else if (choiceOptions.value === "" && trade && trade.security_type && trade.security_type === "Shares"){
      setVisib(false);
      setExpiry("");
      setStrike("");
    }
  }

  const handleGotoEdit = async (e) => {
    e.preventDefault();
    if (selectedRow.length === 1){
      setEditLoading(true);
      setEditButtonInstance(selectedRow);
      const res = await dispatch(
        getTrade({
          trade_id
        })
      );
      checkVisbility(res.payload);
      setTradeID(trade_id);
      setAccountValueUpdateEnable(true);
      // Load tags for this trade
      const tagsResult = await dispatch(getTradeTags({ trade_id }));
      if (tagsResult.payload && tagsResult.payload.tags) {
        setSelectedTagIds(tagsResult.payload.tags.map(t => t.tag_id));
      } else {
        setSelectedTagIds([]);
      }
      // Load existing review for this trade — keep input state empty so placeholders
      // show existing values (mirrors how trade fields use placeholder={trade.X})
      const reviewResult = await dispatch(getTradeReview({ trade_id }));
      if (reviewResult.payload && reviewResult.payload.trade_review_id) {
        setExistingReviewId(reviewResult.payload.trade_review_id);
        setExistingReview(reviewResult.payload);
        setExistingScreenshotUrl(reviewResult.payload.screenshot_url || null);
      } else {
        setExistingReviewId(null);
        setExistingReview(null);
        setExistingScreenshotUrl(null);
      }
      setReviewSetupType("");
      setReviewFollowedPlan("");
      setReviewEmotionalState("");
      setReviewNotes("");
      setReviewScreenshot(null);
      setEditTrade(true);
      setEditLoading(false);
    } else if (selectedRow.length > 1){
      setSelectedTradeIds(selectedRow.map(index => trades.trades[index].trade_id));
      setAccountValueUpdateTradesEnable(true);
      setEditTrade(true);
      //set trade ids like delete trades - DONE
      //logic in html to look at selected rows length - DONE
      //html for this update trades bulk update - DONE
      //API call logic - DONE
      //API in store file - DONE
      //Handle API response - DONE
      //Caching stuff - DONE
    }
    
  };

  const getBulkUpdateCSVComponent = () => (
    <BulkUpdateCsvView
      cancelRef={cancelRef}
      selectedFile={selectedFile}
      csvFixLoading={csvFixLoading}
      tradeLoading={tradeLoading}
      accountValueUpdateCSVEnable={accountValueUpdateCSVEnable}
      isFreeUser={isFreeUser}
      aiCsvQueriesRemaining={aiCsvQueriesRemaining}
      csvQuotaExhausted={csvQuotaExhausted}
      onFileChange={handleFileInputChange}
      onCheckboxChange={handleCheckboxChange}
      onCancel={handleCancelBulkUpdateCsv}
      onConfirm={handleConfirmBulkUpdateCsv}
      onFixWithAI={handleFixBulkUpdateCsv}
    />
  );

  const getEditComponent = () => {
    let content = [];
    if (selectedRow.length === 1){
      content.push(
        <>
        <Box minW={{ base: "90%", md: "468px" }} maxW="650px" rounded="lg" overflow="hidden" boxShadow="sm">
          {tradeLoading ?
            <Stack
                spacing={4}
                p="1rem"
                backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
                boxShadow="md"
              >
              <Center>
              <TradingSpinner size="40px" />
              </Center>
            </Stack>
          :
          <form>
            <Stack
              spacing={4}
              p="1rem"
              backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
              boxShadow="md"
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Heading fontSize="xl" fontWeight="bold" fontFamily="'Open Sans', sans-serif" color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Update Trade</Heading>
                <Button size="sm" variant="ghost" colorScheme="gray" borderRadius="md" onClick={handleClear}>Clear</Button>
              </Flex>
              <Box display="flex">
                <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Trade Type *
                  </FormLabel>
                  <Select value={trade_type} onChange={(e) => setTradeType(e.target.value)}>
                    <option value="" disabled selected>{trade.trade_type}</option>
                    <option>Swing Trade</option>
                    <option>Day Trade</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Security Type *
                  </FormLabel>
                  <Select id="optionsSelection" value={security_type} onChange={(e) => {changeShowOptions(e.target.value); setSecurityType(e.target.value);}}>
                    <option value="" disabled selected>{trade.security_type}</option>
                    <option>Options</option>
                    <option>Shares</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Direction *
                  </FormLabel>
                  <Select value={trade_direction} onChange={(e) => {setTradeDirection(e.target.value);}}>
                    <option value="" disabled selected>{trade.trade_direction}</option>
                    <option>Long</option>
                    <option>Short</option>
                    <option>Call</option>
                    <option>Put</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Ticker *
                  </FormLabel>
                  <div className="ticker-search">
                    <Input type="text" placeholder={trade.ticker_name} value={selectedValue ? selectedValue : searchValue} onChange={handleInputChange} onClick={handleInputClick}/>
                    {isDropdownOpen && (
                      <ul className={colorMode === 'light' ? "search-dropdown" : "search-dropdown search-dropdown-dark"}>
                        {isLoading ? (
                          <div>Loading...</div>
                        ) : (
                          searchResultItems
                        )}
                      </ul>
                    )}
                  </div>
                </FormControl>
              </Box>
              
              {/* Row 2 — 3 fields: Expiry, Strike, Date Trade was Closed */}
              <Box display="flex">
              <FormControl display={visib ? "block" : "none"}>
                <FormLabel fontSize="xs" mb={1} ml={1}>
                  Expiry (Options Only) *
                </FormLabel>
                <Input
                    value={expiry}
                    placeholder={trade.expiry}
                    onFocus={(e) => (e.target.type = "date")}
                    onBlur={(e) => (e.target.type = "text")}
                    max="3900-12-31"
                    min="1900-01-01"
                    onChange={(e) => setExpiry(e.target.value)}
                />
              </FormControl>

              <FormControl display={visib ? "block" : "none"}>
                <FormLabel fontSize="xs" mb={1} ml={1}>
                  Strike Price (Options Only) *
                </FormLabel>
                <InputGroup>
                  <Input
                    value={strike}
                    type="name"
                    placeholder={trade.strike}
                    onChange={(e) => setStrike(e.target.value)}
                  />
                </InputGroup>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" mb={1} ml={1}>
                  Date Trade was Closed *
                </FormLabel>
                <Input
                    value={trade_date}
                    placeholder={trade.trade_date}
                    onFocus={(e) => (e.target.type = "date")}
                    onBlur={(e) => (e.target.type = "text")}
                    max={maxDate}
                    min="1900-01-01"
                    onChange={(e) => setTradeDate(e.target.value)}
                />
              </FormControl>
              </Box>

              {/* Row 3 — 3 fields: Average Cost, # of Units, R/R */}
              <Box display="flex">
              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Average Cost *
                  </FormLabel>
                  <Input
                    value={buy_value}
                    type="name"
                    placeholder={trade.buy_value}
                    onChange={(e) => setBuyValue(e.target.value)}
                  />
              </FormControl>

              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    # of Units *
                  </FormLabel>
                  <Input
                    value={units}
                    type="name"
                    placeholder={trade.units}
                    onChange={(e) => setUnits(e.target.value)}
                  />
              </FormControl>

              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Risk/Reward Ratio (R:R) *
                  </FormLabel>
                  <HStack>
                  <NumberInput
                    value={risk}
                    onChange={(stringValue) => setRisk(stringValue)}
                    //defaultValue = {trade.rr.split(":")[0]}
                    min={1}
                    max={200}
                    inputMode='text'
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text>
                    :
                  </Text>
                  <NumberInput
                    value={reward}
                    onChange={(stringValue) => setReward(stringValue)}
                    //defaultValue = {trade.rr.split(":")[1]}
                    min={1}
                    max={200}
                    inputMode='text'
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  </HStack>
              </FormControl>
              </Box>

              {/* Row 4 — 3 fields: PNL, Fees, % W/L */}
              <Box display="flex">
              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    PNL *
                  </FormLabel>
                  <Input
                    value={pnl}
                    type="name"
                    placeholder={trade.pnl}
                    onChange={(e) => setPNL(e.target.value)}
                  />
              </FormControl>

              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    <HStack spacing={1}>
                      <span>Fees</span>
                      <Tooltip
                        label="Editing fees does NOT re-subtract from PNL. Adjust PNL directly to reflect a new fees value."
                        placement="top"
                        hasArrow
                      >
                        <span>
                          <Icon as={IoInformationCircleOutline} boxSize={3.5} color="gray.500" cursor="help" />
                        </span>
                      </Tooltip>
                    </HStack>
                  </FormLabel>
                  <Input
                    value={fees}
                    type="number"
                    placeholder={trade.fees ?? "0"}
                    onChange={(e) => setFees(e.target.value)}
                  />
              </FormControl>

              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    % Win or Loss *
                  </FormLabel>
                  <Input
                    value={percent_wl}
                    type="name"
                    readOnly
                    placeholder={trade.percent_wl}
                    defaultValue={percent_wl}
                  />
              </FormControl>
              </Box>

              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Comments *
                  </FormLabel>
                  <Textarea value={comments} placeholder={trade.comments} onChange={(e) => setComments(e.target.value)}/>
              </FormControl>
              <AccountValueSyncToggle
                isChecked={accountValueUpdateEnable}
                onChange={(e) => handleCheckboxChange(e, "Update")}
                actionLabel="this trade update"
                pl={2}
              />
              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Tags
                  </FormLabel>
                  <Wrap mb={2}>
                    {allTags.map((tag) => {
                      const isSelected = selectedTagIds.includes(tag.tag_id);
                      const hasCustomColor = tag.color && tag.color !== '#3182CE';
                      return (
                        <WrapItem key={tag.tag_id}>
                          <Tag
                            size="md"
                            variant={isSelected ? "solid" : "outline"}
                            {...(hasCustomColor
                              ? {
                                  bg: isSelected ? tag.color : 'transparent',
                                  color: isSelected ? 'white' : tag.color,
                                  borderColor: tag.color,
                                }
                              : { colorScheme: isSelected ? 'blue' : 'gray' }
                            )}
                            cursor="pointer"
                            onClick={() => handleToggleTag(tag.tag_id)}
                          >
                            <TagLabel>{tag.name}</TagLabel>
                            {isSelected && (
                              <TagCloseButton onClick={(e) => { e.stopPropagation(); handleToggleTag(tag.tag_id); }} />
                            )}
                          </Tag>
                        </WrapItem>
                      );
                    })}
                  </Wrap>
                  <HStack>
                    <Input
                      size="sm"
                      placeholder="Create new tag..."
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                    />
                    <input
                      type="color"
                      value={newTagColor}
                      onChange={(e) => setNewTagColor(e.target.value)}
                      className="color-picker-input"
                    />
                    <Button size="sm" colorScheme="blue" onClick={handleAddTag}>Add</Button>
                  </HStack>
              </FormControl>
              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1} fontWeight="semibold">
                    Post-Trade Review
                  </FormLabel>
              </FormControl>
              <Box display="flex">
              <FormControl>
                <FormLabel fontSize="xs" mb={1} ml={1}>
                  Setup Type
                </FormLabel>
                <Input
                  placeholder={existingReview?.setup_type || "e.g. Breakout, Reversal, Momentum"}
                  value={reviewSetupType}
                  onChange={(e) => setReviewSetupType(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1} ml={1}>
                  Followed Plan?
                </FormLabel>
                <Select
                  value={reviewFollowedPlan}
                  onChange={(e) => setReviewFollowedPlan(e.target.value)}
                >
                  <option value="" disabled>
                    {existingReview?.followed_plan == null
                      ? "-- Select --"
                      : existingReview.followed_plan
                      ? "Yes"
                      : "No"}
                  </option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1} ml={1}>
                  Emotional State
                </FormLabel>
                <Select
                  value={reviewEmotionalState}
                  onChange={(e) => setReviewEmotionalState(e.target.value)}
                >
                  <option value="" disabled>
                    {formatEmotionalState(existingReview?.emotional_state) || "-- Select --"}
                  </option>
                  <option value="calm">Calm</option>
                  <option value="anxious">Anxious</option>
                  <option value="excited">Excited</option>
                  <option value="fearful">Fearful</option>
                  <option value="revenge">Revenge</option>
                </Select>
              </FormControl>
              </Box>
              <FormControl>
                <FormLabel fontSize="xs" mb={1} ml={1}>
                  Review Notes
                </FormLabel>
                <Textarea
                  placeholder={existingReview?.notes || "What would you do differently?"}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={2}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1} ml={1}>
                  Chart Screenshot (optional)
                </FormLabel>
                {existingScreenshotUrl && !reviewScreenshot && (
                  <a href={existingScreenshotUrl} target="_blank" rel="noopener noreferrer">
                    <Image src={existingScreenshotUrl} alt="Current screenshot" maxH="200px" objectFit="contain" mb={2} borderRadius="sm" cursor="pointer" />
                  </a>
                )}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  id="review-screenshot-upload"
                  onChange={(e) => setReviewScreenshot(e.target.files[0] || null)}
                />
                <Button
                  variant="outline"
                  colorScheme="blue"
                  borderRadius="md"
                  size="sm"
                  onClick={() => document.getElementById('review-screenshot-upload').click()}
                >
                  Choose File
                </Button>
                {reviewScreenshot && <Text fontSize="xs" color="gray.500">{reviewScreenshot.name}</Text>}
              </FormControl>
              <Button
                borderRadius="md"
                variant="solid"
                colorScheme="blue"
                width="full"
                onClick={handleDoneEdit}
              >
                Update
              </Button>
              <Button
                borderRadius="md"
                variant="solid"
                colorScheme="gray"
                width="full"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Stack>
          </form>
        }
        </Box>
        </>
      );
    }else if (selectedRow.length > 1){
      content.push(
        <>
        <Box minW={{ base: "90%", md: "468px" }} maxW="650px" rounded="lg" overflow="hidden" boxShadow="sm">
          {tradeLoading ?
            <Stack
                spacing={4}
                p="1rem"
                backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
                boxShadow="md"
              >
              <Center>
              <TradingSpinner size="40px" />
              </Center>
            </Stack>
          :
          <form>
            <Stack
              spacing={4}
              p="1rem"
              backgroundColor={colorMode === 'light' ? "whiteAlpha.900" : "whiteAlpha.100"}
              boxShadow="md"
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Heading fontSize="xl" fontWeight="bold" fontFamily="'Open Sans', sans-serif" color={colorMode === 'light' ? 'gray.700' : 'gray.200'}>Bulk Update Trades</Heading>
                <Button size="sm" variant="ghost" colorScheme="gray" borderRadius="md" onClick={handleClear}>Clear</Button>
              </Flex>
              <Box display="flex">
                <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Trade Type *
                  </FormLabel>
                  <Select value={trade_type} placeholder='Select Trade Type' onChange={(e) => setTradeType(e.target.value)}>
                    <option>Swing Trade</option>
                    <option>Day Trade</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Security Type *
                  </FormLabel>
                  <Select id="optionsSelection" value={security_type} placeholder='Select Security Type' onChange={(e) => {changeShowOptions(e.target.value); setSecurityType(e.target.value)}}>
                    <option>Options</option>
                    <option>Shares</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Direction *
                  </FormLabel>
                  <Select value={trade_direction} placeholder='Select Direction' onChange={(e) => {setTradeDirection(e.target.value)}}>
                    <option>Long</option>
                    <option>Short</option>
                    <option>Call</option>
                    <option>Put</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Ticker *
                  </FormLabel>
                  <div className="ticker-search">
                    <Input type="text" value={selectedValue ? selectedValue : searchValue} onChange={handleInputChange} onClick={handleInputClick}/>
                    {isDropdownOpen && (
                      <ul className={colorMode === 'light' ? "search-dropdown" : "search-dropdown search-dropdown-dark"}>
                        {isLoading ? (
                          <div>Loading...</div>
                        ) : (
                          searchResultItems
                        )}
                      </ul>
                    )}
                  </div>
                </FormControl>
              </Box>
              
              {/* Row 2 — 3 fields: Expiry, Strike, Date Trade was Closed */}
              <Box display="flex">
              <FormControl display={visib ? "block" : "none"}>
                <FormLabel fontSize="xs" mb={1} ml={1}>
                  Expiry (Options Only) *
                </FormLabel>
                <Input
                    value={expiry}
                    type="date"
                    max="3900-12-31"
                    min="1900-01-01"
                    onChange={(e) => setExpiry(e.target.value)}
                />
              </FormControl>

              <FormControl display={visib ? "block" : "none"}>
                <FormLabel fontSize="xs" mb={1} ml={1}>
                  Strike Price (Options Only) *
                </FormLabel>
                <InputGroup>
                  <Input
                    value={strike}
                    type="name"
                    onChange={(e) => setStrike(e.target.value)}
                  />
                </InputGroup>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="xs" mb={1} ml={1}>
                  Date Trade was Closed *
                </FormLabel>
                <Input
                    value={trade_date}
                    type="date"
                    max={maxDate}
                    min="1900-01-01"
                    onChange={(e) => setTradeDate(e.target.value)}
                />
              </FormControl>
              </Box>

              {/* Row 3 — 3 fields: Average Cost, # of Units, R/R */}
              <Box display="flex">
              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Average Cost *
                  </FormLabel>
                  <Input
                    value={buy_value}
                    type="name"
                    onChange={(e) => setBuyValue(e.target.value)}
                  />
              </FormControl>

              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    # of Units *
                  </FormLabel>
                  <Input
                    value={units}
                    type="name"
                    onChange={(e) => setUnits(e.target.value)}
                  />
              </FormControl>

              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Risk/Reward Ratio (R:R) *
                  </FormLabel>
                  <HStack>
                  <NumberInput
                    onChange={(stringValue) => setRisk(stringValue)}
                    value = {risk}
                    min={1}
                    max={200}
                    inputMode='text'
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text>
                    :
                  </Text>
                  <NumberInput
                    onChange={(stringValue) => setReward(stringValue)}
                    value = {reward}
                    min={1}
                    max={200}
                    inputMode='text'
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  </HStack>
              </FormControl>
              </Box>

              {/* Row 4 — 3 fields: PNL, Fees, % W/L */}
              <Box display="flex">
              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    PNL *
                  </FormLabel>
                  <Input
                    value={pnl}
                    type="name"
                    onChange={(e) => setPNL(e.target.value)}
                  />
              </FormControl>

              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    <HStack spacing={1}>
                      <span>Fees</span>
                      <Tooltip
                        label="Editing fees does NOT re-subtract from PNL. Adjust PNL directly to reflect a new fees value."
                        placement="top"
                        hasArrow
                      >
                        <span>
                          <Icon as={IoInformationCircleOutline} boxSize={3.5} color="gray.500" cursor="help" />
                        </span>
                      </Tooltip>
                    </HStack>
                  </FormLabel>
                  <Input
                    value={fees}
                    type="number"
                    placeholder="0"
                    onChange={(e) => setFees(e.target.value)}
                  />
              </FormControl>

              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    % Win or Loss *
                  </FormLabel>
                  <Input
                    value={percent_wl}
                    type="name"
                    readOnly
                    placeholder={"0.00"}
                  />
              </FormControl>
              </Box>

              <FormControl>
                  <FormLabel fontSize="xs" mb={1} ml={1}>
                    Comments *
                  </FormLabel>
                  <Textarea value={comments} placeholder='Reflect on your Trade...' onChange={(e) => setComments(e.target.value)}/>
              </FormControl>
              <AccountValueSyncToggle
                isChecked={accountValueUpdateTradesEnable}
                onChange={(e) => handleCheckboxChange(e, "UpdateTrades")}
                actionLabel="these bulk updates"
                pl={2}
              />
              <Button
                borderRadius="md"
                variant="solid"
                colorScheme="blue"
                width="full"
                onClick={handleDoneEditBulk}
              >
                Update
              </Button>
              <Button
                borderRadius="md"
                variant="outline"
                colorScheme="blue"
                width="full"
                onClick={handleGoToCSVUpdate}
              >
                Update w/ CSV
              </Button>
              <Button
                borderRadius="md"
                variant="solid"
                colorScheme="gray"
                width="full"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Stack>
          </form>
        }
        </Box>
        </>
      );
    }
    return content
  };

  const handleExport = async (e) => {
    setExportLoading(true);
    const exported_trades = trades.trades
    const res = await dispatch(
      exportCsv({
        exported_trades
      })
    );
    const csvData = res.payload;
    // Create a downloadable link in the client's browser
    const downloadLink = document.createElement('a');
    downloadLink.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvData);
    downloadLink.download = 'trades.csv';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    setExportLoading(false);
  };


  const handleDeleteButton = (e) => {
    setSelectedTradeIds(selectedRow.map(index => trades.trades[index].trade_id));
    setDeleteAlertDialog(true);
    setAccountValueDeleteEnable(true);
    onOpen();
  };

  const handleConfirmDelete = async (e) => {
    e.preventDefault();
    await dispatch(
      deleteTrade({
        accountValueDeleteEnable,
        trade_ids
      })
    );
    const filters = {};
    if(filter_trade_type !== ''){
      filters.trade_type = filter_trade_type;
    }
    if(filter_security_type !== ''){
      filters.security_type = filter_security_type;
    }
    if(filter_trade_direction !== ''){
      filters.trade_direction = filter_trade_direction;
    }
    if(filter_ticker_name !== ''){
      filters.ticker_name = filter_ticker_name;
    }
    if(filter_from_date !== '' || filter_to_date !== ''){
      if (filter_from_date !== '' && filter_to_date !== ''){
        filters.from_date = filter_from_date;
        filters.to_date = filter_to_date;
      }else if (filter_from_date !== '' && filter_to_date === ''){
        filters.from_date = filter_from_date;
        filters.to_date = today;
      }
      else if (filter_from_date === '' && filter_to_date !== ''){
        filters.from_date = "1900-01-01";
        filters.to_date = filter_to_date;
      }
    }
    filters.page = 1;
    filters.numrows = num_results;
    window.localStorage.setItem('SummaryFilters', JSON.stringify(filters)); 
    await dispatch(getTradesPage({ filters }));
    handleFilterChange(filtersToQueryString(filters));
    //window.localStorage.setItem('SummaryFilters', JSON.stringify(filters));
    dispatch(
      reset()      
    );
    setSelectedTradeIds([]);
    setSelectedRow([]);
    setDeleteAlertDialog(false);
    onClose();
  };

  const handleCancelDelete = (e) => {
    setDeleteAlertDialog(false);
    onClose();
  };


  const handleToggleTag = (tagId) => {
    if (selectedTagIds.includes(tagId)) {
      setSelectedTagIds(selectedTagIds.filter(id => id !== tagId));
    } else {
      setSelectedTagIds([...selectedTagIds, tagId]);
    }
  };

  const handleAddTag = async () => {
    if (newTagName.trim() === "") return;
    const result = await dispatch(createTag({ name: newTagName.trim(), color: newTagColor }));
    if (result.payload && result.payload.tag_id) {
      setSelectedTagIds([...selectedTagIds, result.payload.tag_id]);
      await dispatch(getUserTags());
    }
    setNewTagName("");
    setNewTagColor("#3182CE");
  };

  const handleDoneEdit = async (e) => {
    e.preventDefault();
    const updateTradeInfo = {
      trade_id,
      trade_type,
      security_type,
      trade_direction,
      ticker_name,
      trade_date,
      expiry,
      strike,
      buy_value,
      units,
      rr,
      pnl,
      percent_wl,
      fees,
      comments
    };
    await dispatch(
        update({
          accountValueUpdateEnable,
          trade_id,
          trade_type,
          security_type,
          trade_direction,
          ticker_name,
          trade_date,
          expiry,
          strike,
          buy_value,
          units,
          rr,
          pnl,
          percent_wl,
          fees,
          comments
        })
      );
    // Save tags for the trade
    if (trade_id) {
      await dispatch(setTradeTags({ trade_id, tag_ids: selectedTagIds }));
    }
    // Save or update trade review
    if (trade_id) {
      const hasReviewData = reviewSetupType || reviewFollowedPlan !== "" || reviewEmotionalState || reviewNotes;
      if (existingReviewId) {
        // Update existing review — fall back to existing values when state is blank,
        // mirroring the trade-edit pattern where blank inputs preserve existing data.
        await dispatch(editTradeReview({
          trade_review_id: existingReviewId,
          setup_type: reviewSetupType !== "" ? reviewSetupType : (existingReview?.setup_type ?? ""),
          followed_plan: reviewFollowedPlan !== ""
            ? reviewFollowedPlan === "true"
            : (existingReview?.followed_plan ?? ""),
          emotional_state: reviewEmotionalState !== "" ? reviewEmotionalState : (existingReview?.emotional_state ?? ""),
          notes: reviewNotes !== "" ? reviewNotes : (existingReview?.notes ?? ""),
        }));
        if (reviewScreenshot) {
          await dispatch(uploadReviewScreenshot({
            trade_review_id: existingReviewId,
            selectedFile: reviewScreenshot,
          }));
        }
      } else if (hasReviewData) {
        // Create new review
        const reviewResult = await dispatch(createTradeReview({
          trade_id,
          setup_type: reviewSetupType || null,
          followed_plan: reviewFollowedPlan === "" ? null : reviewFollowedPlan === "true",
          emotional_state: reviewEmotionalState || null,
          notes: reviewNotes || null,
        }));
        if (reviewScreenshot && reviewResult.payload && reviewResult.payload.trade_review_id) {
          await dispatch(uploadReviewScreenshot({
            trade_review_id: reviewResult.payload.trade_review_id,
            selectedFile: reviewScreenshot,
          }));
        }
      }
    }
    window.localStorage.setItem('updateTradeInfo', JSON.stringify(updateTradeInfo));
  };

  const handleDoneEditBulk = async (e) => {
    e.preventDefault();
    const ids = trade_ids;
    const updateBulkTradeInfo = {
      accountValueUpdateTradesEnable,
      trade_ids,
      trade_type,
      security_type,
      trade_direction,
      ticker_name,
      trade_date,
      expiry,
      strike,
      buy_value,
      units,
      rr,
      pnl,
      percent_wl,
      fees,
      comments
    };
    let update_info = {
      trade_type,
      security_type,
      trade_direction,
      ticker_name,
      trade_date,
      expiry,
      strike,
      buy_value,
      units,
      rr,
      pnl,
      percent_wl,
      fees,
      comments
    }
    await dispatch(
      updateTrades({
          accountValueUpdateTradesEnable,
          ids,
          update_info
        })
      );
    window.localStorage.setItem('updateBulkTradeInfo', JSON.stringify(updateBulkTradeInfo));
  };

  const handleCancel = (e) => {
    e.preventDefault();
    dispatch(
      reset()
    );
    setEditTrade(false);
    clearFormStates();
    setSelectedRow([]);
    setTradeID(null);
    setIsLoading(false);
    setSearchValue('');
    setIsDropdownOpen(false);
  }

  const handleGoToCSVUpdate = (e) => {
    e.preventDefault();
    dispatch(
      reset()
    );
    setEditTrade(false);
    clearFormStates();
    setSelectedRow([]);
    setTradeID(null);
    setIsLoading(false);
    setSearchValue('');
    setIsDropdownOpen(false);
    setAccountValueUpdateCSVEnable(true);
    setSelectedFile(null); // Clear any file from previous import dialog
    setBulkUpdateWithCSV(true);
  }

  const handleCancelBulkUpdateCsv = (e) => {
    setBulkUpdateWithCSV(false);
    setSelectedFile(null);
  }

  const handleClear = (e) => {
    e.preventDefault();
    clearFormStates(false);
    setIsLoading(false);
    setSearchValue('');
    setIsDropdownOpen(false);
    changeShowOptions(e);
  }

  const handleNextPage = async (e) => {
    if(nextPageEnable){
      const filters = {};
      if(filter_trade_type !== ''){
        filters.trade_type = filter_trade_type;
      }
      if(filter_security_type !== ''){
        filters.security_type = filter_security_type;
      }
      if(filter_trade_direction !== ''){
        filters.trade_direction = filter_trade_direction;
      }
      if(filter_ticker_name !== ''){
        filters.ticker_name = filter_ticker_name;
      }
      if(filter_from_date !== '' || filter_to_date !== ''){
        if (filter_from_date !== '' && filter_to_date !== ''){
          filters.from_date = filter_from_date;
          filters.to_date = filter_to_date;
        }else if (filter_from_date !== '' && filter_to_date === ''){
          filters.from_date = filter_from_date;
          filters.to_date = today;
        }
        else if (filter_from_date === '' && filter_to_date !== ''){
          filters.from_date = "1900-01-01";
          filters.to_date = filter_to_date;
        }
      }
      filters.page = page+1;
      filters.numrows = num_results;
      window.localStorage.setItem('SummaryFilters', JSON.stringify(filters)); 
      await dispatch(getTradesPage({ filters }));
      handleFilterChange(filtersToQueryString(filters));
      //window.localStorage.setItem('SummaryFilters', JSON.stringify(filters));
      setSelectedRow([]);
    }
  }

  const handleBackPage = async (e) => {
    if(backPageEnable){
      const filters = {};
      if(filter_trade_type !== ''){
        filters.trade_type = filter_trade_type;
      }
      if(filter_security_type !== ''){
        filters.security_type = filter_security_type;
      }
      if(filter_trade_direction !== ''){
        filters.trade_direction = filter_trade_direction;
      }
      if(filter_ticker_name !== ''){
        filters.ticker_name = filter_ticker_name;
      }
      if(filter_from_date !== '' || filter_to_date !== ''){
        if (filter_from_date !== '' && filter_to_date !== ''){
          filters.from_date = filter_from_date;
          filters.to_date = filter_to_date;
        }else if (filter_from_date !== '' && filter_to_date === ''){
          filters.from_date = filter_from_date;
          filters.to_date = today;
        }
        else if (filter_from_date === '' && filter_to_date !== ''){
          filters.from_date = "1900-01-01";
          filters.to_date = filter_to_date;
        }
      }
      filters.page = page-1;
      filters.numrows = num_results;
      window.localStorage.setItem('SummaryFilters', JSON.stringify(filters)); 
      await dispatch(getTradesPage({ filters }));  
      handleFilterChange(filtersToQueryString(filters));
      //window.localStorage.setItem('SummaryFilters', JSON.stringify(filters));
      setSelectedRow([]);
    }
  }

  const handleChangeNumResults = async (e) => {
    const new_num_results = e.target.value;
    setNumResults(new_num_results);
    const filters = {};
    if(filter_trade_type !== ''){
      filters.trade_type = filter_trade_type;
    }
    if(filter_security_type !== ''){
      filters.security_type = filter_security_type;
    }
    if(filter_trade_direction !== ''){
      filters.trade_direction = filter_trade_direction;
    }
    if(filter_ticker_name !== ''){
      filters.ticker_name = filter_ticker_name;
    }
    if(filter_from_date !== '' || filter_to_date !== ''){
      if (filter_from_date !== '' && filter_to_date !== ''){
        filters.from_date = filter_from_date;
        filters.to_date = filter_to_date;
      }else if (filter_from_date !== '' && filter_to_date === ''){
        filters.from_date = filter_from_date;
        filters.to_date = today;
      }
      else if (filter_from_date === '' && filter_to_date !== ''){
        filters.from_date = "1900-01-01";
        filters.to_date = filter_to_date;
      }
    }
    filters.page = 1;
    filters.numrows = new_num_results;
    window.localStorage.setItem('SummaryFilters', JSON.stringify(filters)); 
    await dispatch(getTradesPage({ filters }));  
    handleFilterChange(filtersToQueryString(filters));
    //window.localStorage.setItem('SummaryFilters', JSON.stringify(filters));
    setSelectedRow([]);
  }

  const handleInputTickerFIlterClick = (event) => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const getFilterComponent = () => (
    <TradeFilters
      filters={filters}
      tags={allTags}
      onRemoveFilter={handleRemoveFilter}
      labelMap={filterLabelMap}
      keysToSkip={keysToSkip}
      toggleFilter={toggleFilter}
      onToggleFilter={() => setToggleFilter(!toggleFilter)}
      filter_trade_type={filter_trade_type}
      setFilterTradeType={setFilterTradeType}
      filter_security_type={filter_security_type}
      setFilterSecurityType={setFilterSecurityType}
      filter_trade_direction={filter_trade_direction}
      setFilterTradeDirection={setFilterTradeDirection}
      filter_from_date={filter_from_date}
      setFilterFromDate={setFilterFromDate}
      filter_to_date={filter_to_date}
      setFilterToDate={setFilterToDate}
      filter_tag={filter_tag}
      setFilterTag={setFilterTag}
      searchTickerValue={searchTickerValue}
      selectedTickerValue={selectedTickerValue}
      onTickerInputChange={handleInputTickerFilterChange}
      onTickerInputClick={handleInputTickerFIlterClick}
      isDropdownOpen={isDropdownOpen}
      isLoading={isLoading}
      searchTickerResultItems={searchTickerResultItems}
      maxDate={maxDate}
      onSubmit={handleSubmitFilter}
      onClear={handleClearFilter}
    />
  );

  const handleSubmitFilter = async (e) => {
    e.preventDefault();
    const filters = {};
    if(filter_trade_type !== ''){
      filters.trade_type = filter_trade_type;
    }
    if(filter_security_type !== ''){
      filters.security_type = filter_security_type;
    }
    if(filter_trade_direction !== ''){
      filters.trade_direction = filter_trade_direction;
    }
    if(filter_ticker_name !== ''){
      filters.ticker_name = filter_ticker_name;
    }
    if(filter_from_date !== '' || filter_to_date !== ''){
      if (filter_from_date !== '' && filter_to_date !== ''){
        filters.from_date = filter_from_date;
        filters.to_date = filter_to_date;
      }else if (filter_from_date !== '' && filter_to_date === ''){
        filters.from_date = filter_from_date;
        filters.to_date = today;
      }
      else if (filter_from_date === '' && filter_to_date !== ''){
        filters.from_date = "1900-01-01";
        filters.to_date = filter_to_date;
      }
    }
    if(filter_tag !== ''){
      filters.tag_id = filter_tag;
    }
    filters.page = 1;
    filters.numrows = num_results;
    window.localStorage.setItem('SummaryFilters', JSON.stringify(filters));
    await dispatch(getTradesPage({ filters }));
    handleFilterChange(filtersToQueryString(filters));
    //window.localStorage.setItem('SummaryFilters', JSON.stringify(filters)); 
    setSelectedRow([]);
    setFilters(filters);
    //setToggleFilter(!toggleFilter);
  }

  const handleClearFilter = async (e) => {
    e.preventDefault();
    setFilterTradeType('');
    setFilterSecurityType('');
    setFilterTickerName('');
    setSearchTickerValue('');
    setSelectedTickerValue('');
    setFilterFromDate('');
    setFilterToDate('');
    setFilterTag('');
    window.localStorage.removeItem('SummaryFilters');
    setFilters({});
    const filters = {};
    filters.page = 1;
    filters.numrows = num_results;
    await dispatch(getTradesPage({ filters }));
    handleFilterChange(filtersToQueryString({}));
    setSelectedRow([]);
    //setToggleFilter(!toggleFilter);
  }

  const handleLogTrade = (e) => {
    navigate("/logTrade");
  }

  useEffect(() => {
    calculatePercent();
  }, [pnl, buy_value, units]); 

  const calculatePercent = () => {
    const pnlFloat = parseFloat(pnl);
    const buyValueFloat = parseFloat(buy_value);
    const unitsFloat = parseFloat(units);

    // Form field values (what the user has typed)
    const hasPnl = !isNaN(pnlFloat);
    const hasBuyValue = !isNaN(buyValueFloat) && buyValueFloat !== 0;
    const hasUnits = !isNaN(unitsFloat) && unitsFloat !== 0;

    // Existing trade values — explicitly guard against null since isNaN(null) === false in JS
    const hasExistingPnl = trade?.pnl != null && !isNaN(parseFloat(trade.pnl));
    const hasExistingBuyValue = trade?.buy_value != null && !isNaN(parseFloat(trade.buy_value)) && parseFloat(trade.buy_value) !== 0;
    const hasExistingUnits = trade?.units != null && !isNaN(parseFloat(trade.units)) && parseFloat(trade.units) !== 0;

    // Use form value if present, otherwise fall back to existing trade value
    const effectivePnl = hasPnl ? pnlFloat : (hasExistingPnl ? parseFloat(trade.pnl) : null);
    const effectiveBuyValue = hasBuyValue ? buyValueFloat : (hasExistingBuyValue ? parseFloat(trade.buy_value) : null);
    const effectiveUnits = hasUnits ? unitsFloat : (hasExistingUnits ? parseFloat(trade.units) : null);

    if (effectivePnl != null && effectiveBuyValue != null && effectiveUnits != null) {
      setPercentWL(((effectivePnl / (effectiveBuyValue * effectiveUnits)) * 100).toFixed(2));
    } else {
      setPercentWL("");
    }
  }

  const handleImportButton = (e) => {
    setAccountValueImportEnable(true);
    setSelectedFile(null); // Clear any file from previous update dialog
    setImportDialog(true);
  };

  const handleCancelImportCsv = (e) => {
    setImportDialog(false);
    setSelectedFile(null);
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "text/csv") {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
      alert("Please select a CSV file.");
    }
  };

  const handleFixCsv = async () => {
    if (!selectedFile) return;
    
    setCsvFixLoading(true);
    setCsvFixIsBulkUpdate(false); // Mark that this is for import, not bulk update
    try {
      const result = await dispatch(fixCsvImport({ csvFile: selectedFile }));
      
      // Check if the request was successful (not rejected by Redux)
      if (result.type === 'auth/fixCsvImport/rejected') {
        const { title, ...toastOpts } = resolveCsvFixRejection(result.payload, {
          fallbackDescription: "Could not fix CSV. Try importing without the agent.",
        });
        showToast(title, toastOpts);
        setCsvFixLoading(false);
        setCsvFixIsBulkUpdate(false);
        return;
      }

      // Check the status from the backend response
      if (result.payload && result.payload.status === "completed") {
        // The backend now returns structured data directly
        const {
          format_detected,
          issues_found,
          fixes_applied,
          remaining_issues,
          fixed_csv_content,
          can_auto_fix
        } = result.payload;

        setCsvFixResult({
          format_detected,
          issues_found: issues_found || [],
          fixes_applied: fixes_applied || [],
          remaining_issues: remaining_issues || [],
          fixed_csv_content,
          can_auto_fix: can_auto_fix || false
        });

        if (fixed_csv_content) {
          setFixedCsvContent(fixed_csv_content);
          // Parse CSV content into 2D array for editing
          const csvRows = fixed_csv_content.split('\n').filter(row => row.trim());
          const csvData = csvRows.map(row => {
            // Simple CSV parsing - handles quoted fields
            const result = [];
            let current = '';
            let inQuotes = false;
            for (let i = 0; i < row.length; i++) {
              const char = row[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            result.push(current.trim());
            return result;
          });
          setEditableCsvData(csvData);
          setCsvFixDialog(true);
          setImportDialog(false);
          setSelectedFile(null); // Clear file after agent returns results
        } else {
          showToast("CSV Fix Incomplete", {

            description: "Agent completed but no fixed CSV content was generated. Check the issues below.",

            status: "warning",

            duration: 5000,
          });
          setCsvFixDialog(true);
          setImportDialog(false);
          setSelectedFile(null); // Clear file after agent returns results
        }
      } else if (result.payload && result.payload.status === "timeout") {
        showToast("CSV Fix Timeout", {

          description: result.payload.error || "Agent execution took too long. Try importing directly or with a simpler CSV.",

          status: "warning",

          duration: 5000,
        });
        setCsvFixResult({
          format_detected: "unknown",
          issues_found: [],
          fixes_applied: [],
          remaining_issues: result.payload.remaining_issues || [{ type: "timeout", message: "Agent execution limit reached" }],
          fixed_csv_content: null,
          can_auto_fix: false
        });
        setCsvFixDialog(true);
        setImportDialog(false);
        setSelectedFile(null); // Clear file after agent returns results
      } else if (result.payload && result.payload.status === "error") {
        // Backend returned an error
        showToast("CSV Fix Failed", {

          description: result.payload.error || "Could not fix CSV. Try importing without the agent.",

          status: "error",

          duration: 5000,
        });
        setCsvFixResult({
          format_detected: "unknown",
          issues_found: [],
          fixes_applied: [],
          remaining_issues: result.payload.remaining_issues || [{ type: "error", message: result.payload.error || "Unknown error" }],
          fixed_csv_content: null,
          can_auto_fix: false
        });
        setCsvFixDialog(true);
        setImportDialog(false);
        setSelectedFile(null); // Clear file after agent returns results
      } else {
        // Unexpected response format
        showToast("CSV Fix Failed", {

          description: "Unexpected response format. Try importing without the agent.",

          status: "warning",

          duration: 5000,
        });
        setCsvFixLoading(false);
        setCsvFixIsBulkUpdate(false);
        setSelectedFile(null); // Clear file after agent returns results
      }
    } catch (error) {
      showToast("Error", {

        description: "Error fixing CSV. Try importing without the agent.",

        status: "error",

        duration: 5000,
      });
      setCsvFixLoading(false);
      setCsvFixIsBulkUpdate(false);
      setSelectedFile(null); // Clear file on error
    } finally {
      setCsvFixLoading(false);
    }
  };

  const handleFixBulkUpdateCsv = async () => {
    if (!selectedFile) return;
    
    setCsvFixLoading(true);
    setCsvFixIsBulkUpdate(true); // Mark that this is for bulk update
    try {
      const result = await dispatch(fixCsvImport({ csvFile: selectedFile }));
      
      // Check if the request was successful (not rejected by Redux)
      if (result.type === 'auth/fixCsvImport/rejected') {
        const { title, ...toastOpts } = resolveCsvFixRejection(result.payload, {
          fallbackDescription: "Could not fix CSV. Try updating without the agent.",
        });
        showToast(title, toastOpts);
        setCsvFixLoading(false);
        setCsvFixIsBulkUpdate(false);
        return;
      }

      // Check the status from the backend response
      if (result.payload && result.payload.status === "completed") {
        // The backend now returns structured data directly
        const {
          format_detected,
          issues_found,
          fixes_applied,
          remaining_issues,
          fixed_csv_content,
          can_auto_fix
        } = result.payload;
        
        setCsvFixResult({
          format_detected,
          issues_found: issues_found || [],
          fixes_applied: fixes_applied || [],
          remaining_issues: remaining_issues || [],
          fixed_csv_content,
          can_auto_fix: can_auto_fix || false
        });
        
        if (fixed_csv_content) {
          setFixedCsvContent(fixed_csv_content);
          // Parse CSV content into 2D array for editing
          const csvRows = fixed_csv_content.split('\n').filter(row => row.trim());
          const csvData = csvRows.map(row => {
            // Simple CSV parsing - handles quoted fields
            const result = [];
            let current = '';
            let inQuotes = false;
            for (let i = 0; i < row.length; i++) {
              const char = row[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            result.push(current.trim());
            return result;
          });
          setEditableCsvData(csvData);
          setCsvFixDialog(true);
          setSelectedFile(null); // Clear file after agent returns results
        } else {
          showToast("CSV Fix Incomplete", {

            description: "Agent completed but no fixed CSV content was generated. Check the issues below.",

            status: "warning",

            duration: 5000,
          });
          setCsvFixDialog(true);
          setSelectedFile(null); // Clear file after agent returns results
        }
      } else if (result.payload && result.payload.status === "timeout") {
        showToast("CSV Fix Timeout", {

          description: result.payload.error || "Agent execution took too long. Try updating directly or with a simpler CSV.",

          status: "warning",

          duration: 5000,
        });
        setCsvFixResult({
          format_detected: "unknown",
          issues_found: [],
          fixes_applied: [],
          remaining_issues: result.payload.remaining_issues || [{ type: "timeout", message: "Agent execution limit reached" }],
          fixed_csv_content: null,
          can_auto_fix: false
        });
        setCsvFixDialog(true);
        setSelectedFile(null); // Clear file after agent returns results
      } else if (result.payload && result.payload.status === "error") {
        // Backend returned an error
        showToast("CSV Fix Failed", {

          description: result.payload.error || "Could not fix CSV. Try updating without the agent.",

          status: "error",

          duration: 5000,
        });
        setCsvFixResult({
          format_detected: "unknown",
          issues_found: [],
          fixes_applied: [],
          remaining_issues: result.payload.remaining_issues || [{ type: "error", message: result.payload.error || "Unknown error" }],
          fixed_csv_content: null,
          can_auto_fix: false
        });
        setCsvFixDialog(true);
        setSelectedFile(null); // Clear file after agent returns results
      } else {
        // Unexpected response format
        showToast("CSV Fix Failed", {

          description: "Unexpected response format. Try updating without the agent.",

          status: "warning",

          duration: 5000,
        });
        setCsvFixLoading(false);
        setCsvFixIsBulkUpdate(false);
        setSelectedFile(null); // Clear file after agent returns results
      }
    } catch (error) {
      showToast("Error", {

        description: "Error fixing CSV. Try updating without the agent.",

        status: "error",

        duration: 5000,
      });
      setCsvFixLoading(false);
      setCsvFixIsBulkUpdate(false);
      setSelectedFile(null); // Clear file on error
    } finally {
      setCsvFixLoading(false);
    }
  };

  const handleConfirmImportCsvDirect = async (fileToImport = null) => {
    // If fileToImport is a valid File or Blob, use it; otherwise use selectedFile from state
    // This handles cases where an event object might be accidentally passed
    const fileToUse = (fileToImport instanceof File || fileToImport instanceof Blob) ? fileToImport : selectedFile;
    if (!fileToUse) {
      showToast("No File Selected", {

        description: "Please select a CSV file to import.",

        status: "warning",

        duration: 3000,
      });
      return;
    }
    
    console.log(fileToUse);
    // Ensure the file is a valid File or Blob object
    if (!(fileToUse instanceof File) && !(fileToUse instanceof Blob)) {
      showToast("Invalid File", {

        description: "The file object is not valid. Please try selecting the file again.",

        status: "error",

        duration: 3000,
      });
      return;
    }
    
    await dispatch(
      importCsv({
        accountValueImportEnable,
        selectedFile: fileToUse
      })
    );
    setImportDialog(false);
    setCsvFixDialog(false);
    setCsvFixResult(null);
    setFixedCsvContent(null);
    const filters = {};
    if(filter_trade_type !== ''){
      filters.trade_type = filter_trade_type;
    }
    if(filter_security_type !== ''){
      filters.security_type = filter_security_type;
    }
    if(filter_trade_direction !== ''){
      filters.trade_direction = filter_trade_direction;
    }
    if(filter_ticker_name !== ''){
      filters.ticker_name = filter_ticker_name;
    }
    if(filter_from_date !== '' || filter_to_date !== ''){
      if (filter_from_date !== '' && filter_to_date !== ''){
        filters.from_date = filter_from_date;
        filters.to_date = filter_to_date;
      }else if (filter_from_date !== '' && filter_to_date === ''){
        filters.from_date = filter_from_date;
        filters.to_date = today;
      }
      else if (filter_from_date === '' && filter_to_date !== ''){
        filters.from_date = "1900-01-01";
        filters.to_date = filter_to_date;
      }
    }
    filters.page = 1;
    filters.numrows = num_results;
    window.localStorage.setItem('SummaryFilters', JSON.stringify(filters)); 
    await dispatch(getTradesPage({ filters }));
    handleFilterChange(filtersToQueryString(filters));
    //window.localStorage.setItem('SummaryFilters', JSON.stringify(filters)); 
    setSelectedFile(null);
  };

  const downloadErrorCsv = (errorIds) => {
    // Define CSV headers with trade_id first
    const headers = ['trade_id', 'error_message'];
  
    // Function to properly escape any field with commas
    const escapeCsvField = (field) => {
      // Add double quotes around the field if it contains a comma
      return field.includes(',') ? `"${field}"` : field;
    };
  
    // Create CSV content with trade_id first and error_message second
    const csvContent = [
      headers.join(','),  // CSV headers as the first row
      ...errorIds.map(row => 
        [
          row.trade_id,  // trade_id comes first
          escapeCsvField(row.error_message)  // error_message comes second
        ].join(',')
      )  // Map each errorId into a row and handle comma escaping
    ].join('\n');  // Join each row with a newline
  
    // Create a blob from the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv' });
  
    // Create a temporary link to trigger file download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'errors.csv';  // Filename for the downloaded CSV
    document.body.appendChild(a);
    a.click();
    a.remove();  // Clean up the link after triggering the download
  };

  const handleConfirmBulkUpdateCsv = async (fileToImport = null) => {
    // If fileToImport is a valid File or Blob, use it; otherwise use selectedFile from state
    const fileToUse = (fileToImport instanceof File || fileToImport instanceof Blob) ? fileToImport : selectedFile;
    if (!fileToUse) {
      showToast("No File Selected", {

        description: "Please select a CSV file to update.",

        status: "warning",

        duration: 3000,
      });
      return;
    }
    
    const res = await dispatch(
      bulkUpdateCsv({
        accountValueUpdateCSVEnable,
        selectedFile: fileToUse
      })
    );
    const data = await res.payload;
    // Automatically download CSV if there are error IDs
    if (data.error_ids && data.error_ids.length > 0) {
      downloadErrorCsv(data.error_ids);
    }
    setImportDialog(false);
    const filters = {};
    if(filter_trade_type !== ''){
      filters.trade_type = filter_trade_type;
    }
    if(filter_security_type !== ''){
      filters.security_type = filter_security_type;
    }
    if(filter_trade_direction !== ''){
      filters.trade_direction = filter_trade_direction;
    }
    if(filter_ticker_name !== ''){
      filters.ticker_name = filter_ticker_name;
    }
    if(filter_from_date !== '' || filter_to_date !== ''){
      if (filter_from_date !== '' && filter_to_date !== ''){
        filters.from_date = filter_from_date;
        filters.to_date = filter_to_date;
      }else if (filter_from_date !== '' && filter_to_date === ''){
        filters.from_date = filter_from_date;
        filters.to_date = today;
      }
      else if (filter_from_date === '' && filter_to_date !== ''){
        filters.from_date = "1900-01-01";
        filters.to_date = filter_to_date;
      }
    }
    filters.page = 1;
    filters.numrows = num_results;
    window.localStorage.setItem('SummaryFilters', JSON.stringify(filters)); 
    await dispatch(getTradesPage({ filters }));
    handleFilterChange(filtersToQueryString(filters));
    //window.localStorage.setItem('SummaryFilters', JSON.stringify(filters)); 
    setSelectedFile(null);
    setBulkUpdateWithCSV(false);
  };

  const handleSelectAll = (e) => {
    if (hasTrades && trades.trades.length === 1) {
      setSelectedRow([]);
      setTradeID(trades.trades[0].trade_id);
      setSelectedRow([...selectedRow, 0]);
    }else if (hasTrades && trades.trades.length > 1){
      setSelectedRow([]);
      setTradeID(null);
      setSelectedRow(Array.from({ length: trades.trades.length }, (_, index) => index));
    }
  }

  const handleClearSelected = (e) => {
    setSelectedRow([]);
    setTradeID(null);
  }


  useEffect(() => {
    const fetchSearchResults = async () => {
      setIsLoading(true);
      const response = await axios.get(`https://ticker-2e1ica8b9.now.sh/keyword/${searchValue}`);
      const topResults = response.data.map(f => [f.symbol + ", " + f.name]);
      setSearchResults(topResults);
      setIsLoading(false);
    };

    if (searchValue) {
      fetchSearchResults();
      setIsDropdownOpen(true);
    } else {
      setSearchResults([]);
      setIsDropdownOpen(false);
    }
  }, [searchValue]);

  const handleInputChange = (event) => {
    setSelectedValue('');
    setSearchValue(event.target.value);
    setTickerName(event.target.value);
  };

  const handleInputClick = (event) => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleInputTickerFilterChange = (event) => {
    setSelectedTickerValue('');
    setSearchTickerValue(event.target.value);
    //setFilterTickerName(event.target.value);
  };

  const handleSelection = (selection) => {
    const selectionString = selection[0];
    const index = selectionString.indexOf(",");
    const newTicker = selectionString.substring(0,index);
    setSelectedValue(newTicker);
    setTickerName(newTicker);
    setIsDropdownOpen(false);
  };

  /* need to parse out ticker and send it to handleSelection */
  const searchResultItems = searchResults.map((result) => (
    <li key={result} onClick={() => handleSelection(result)}> 
      {result}
    </li>
  ));

  const handleTickerSelection = (selection) => {
    const selectionString = selection[0];
    setSelectedTickerValue(selectionString);
    setFilterTickerName(selectionString);
    setIsDropdownOpen(false);
  };

  const searchTickerResultItems = searchTickerResults.map((result) => (
    <li key={result} onClick={() => handleTickerSelection(result)}> 
      {result}
    </li>
  ));

  // grabbing current date to set a max to the birthday input
  const currentDate = new Date();
  let [month, day, year] = currentDate.toLocaleDateString().split("/");
  // input max field must have 08 instead of 8
  month = month.length === 2 ? month : "0" + month;
  day = day.length === 2 ? day : "0" + day;
  const maxDate = year + "-" + month + "-" + day;  
  
  //sorting logic
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedTrades = React.useMemo(() => {
    if(hasTrades) {
      let sortableItems = [...trades.trades];
      if (sortConfig.key !== null) {
        sortableItems.sort((a, b) => {
          const aValue = a[sortConfig.key];
          const bValue = b[sortConfig.key];
  
          // Handle null values
          if (aValue === null) return sortConfig.direction === 'asc' ? -1 : 1;
          if (bValue === null) return sortConfig.direction === 'asc' ? 1 : -1;
  
          if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }
      return sortableItems;
    }
  }, [trades, sortConfig]);

  const requestSort = key => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? <TriangleUpIcon /> : <TriangleDownIcon />;
    }
    return <IoFilter />;
  };

  const handleTableRowClick = async (trade, index) => {
    if (selectedRow.includes(index)) {
      const filteredSelectedRow = selectedRow.filter((row) => row !== index);
      await setSelectedRow(filteredSelectedRow);
      if (selectedRow.length === 2) {
        const indexofIndex = selectedRow.indexOf(index);
        if (indexofIndex === 0) {
          setTradeID(trades.trades[selectedRow[1]].trade_id);
        } else {
          setTradeID(trades.trades[selectedRow[0]].trade_id);
        }
      } else if (selectedRow.length === 1) {
        setTradeID(null);
      }
    } else if (!selectedRow.includes(index) && selectedRow.length === 0) {
      setSelectedRow([...selectedRow, index]);
      setTradeID(trade.trade_id);
    } else if (!selectedRow.includes(index) && selectedRow.length >= 1) {
      setSelectedRow([...selectedRow, index]);
      setTradeID(null);
    }
  };

  return (
    !editTrade && !bulkUpdateWithCSV ? (
      <Flex
        flexDirection="column"
        height="100vh"
        backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}
      >
        <Box flex="auto" overflowX="auto" overflowY="auto">
          <AnimatePresence mode="wait">
          {(authLoading || !tradesLoaded) && !csvFixLoading ?
            <motion.div key="skeleton" initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
              <VStack flex="auto" p={4} spacing={4} w="full" align="stretch">
                <SummarySkeleton />
                {csvFixLoading && (
                  <Center>
                    <VStack spacing={4}>
                      <TradingSpinner size="40px" />
                      <Text fontSize="md" fontWeight="semibold" textAlign="center">
                        AI Agent is running...
                      </Text>
                      <Text fontSize="sm" color={colorMode === 'light' ? "gray.600" : "gray.400"} textAlign="center">
                        This may take up to 2 minutes
                      </Text>
                    </VStack>
                  </Center>
                )}
              </VStack>
            </motion.div>
          :
            <motion.div key="content" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}>
            <VStack flex="auto" p={4} spacing={4} w="full" align="stretch">
            {/* Filter Bar */}
            <Box mb={-2}>{getFilterComponent()}</Box>

            {csvFixLoading && (
              <Center>
                <VStack spacing={4}>
                  <TradingSpinner size="40px" />
                  <Text fontSize="md" fontWeight="semibold" textAlign="center">
                    AI Agent is running...
                  </Text>
                  <Text fontSize="sm" color={colorMode === 'light' ? "gray.600" : "gray.400"} textAlign="center">
                    This may take up to 2 minutes
                  </Text>
                </VStack>
              </Center>
            )}

            {/* Action Buttons */}
            <HStack spacing={2} flexWrap="wrap">
            <Button size="sm" variant="outline" colorScheme="gray" borderRadius="md" borderColor={colorMode === 'light' ? 'gray.300' : undefined} onClick={(e) => handleSelectAll(e.target.value)}>
              Select All
            </Button>
            <Button size="sm" variant="outline" colorScheme="gray" borderRadius="md" borderColor={colorMode === 'light' ? 'gray.300' : undefined} onClick={(e) => handleClearSelected(e.target.value)}>
              Clear
            </Button>
            <Spacer />
            <Button size="sm" variant="outline" colorScheme="gray" borderRadius="md" borderColor={colorMode === 'light' ? 'gray.300' : undefined} onClick={(e) => handleImportButton(e.target.value)}>
              Import
            </Button>
            {importCsvDialog}
              <AlertDialog
              motionPreset='slideInBottom'
              isOpen={importCsvDialog}
              leastDestructiveRef={cancelRef}
              onClose={e => handleCancelImportCsv(e)}
              isCentered={true}
              size='5xl'
              closeOnOverlayClick={true}
            >
              <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  Import Trades from CSV
                </AlertDialogHeader>

                <AlertDialogBody>
                  <VStack align="stretch" spacing={5}>
                    {/* Required columns */}
                    <Box>
                      <Heading size='xs' textTransform='uppercase' color={colorMode === 'light' ? 'gray.600' : 'gray.300'} mb={2}>
                        Required Columns
                      </Heading>
                      <Wrap spacing={2}>
                        {['security_type', 'ticker_name', 'execution_time', 'side', 'quantity', 'cost_basis'].map((col) => (
                          <WrapItem key={col}>
                            <Tag size="md" colorScheme="blue" variant="subtle" fontFamily="mono">{col}</Tag>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>

                    {/* Optional columns */}
                    <Box>
                      <Heading size='xs' textTransform='uppercase' color={colorMode === 'light' ? 'gray.600' : 'gray.300'} mb={2}>
                        Optional Columns
                      </Heading>
                      <Wrap spacing={2}>
                        {[
                          { name: 'expiry', tip: 'Required when security_type is PUT or CALL.' },
                          { name: 'strike', tip: 'Required when security_type is PUT or CALL.' },
                          { name: 'fees', tip: 'Per-leg commissions + regulatory fees. Summed across the matching BUY and SELL rows; PNL is stored net of fees. Two-column brokers (Fidelity Est. Comm + Est. Fee, E*TRADE, tastytrade) are handled by the AI Update flow.' },
                        ].map(({ name, tip }) => (
                          <WrapItem key={name}>
                            <Tooltip label={tip} placement="top" hasArrow>
                              <Tag size="md" colorScheme="gray" variant="subtle" fontFamily="mono" cursor="help">{name}</Tag>
                            </Tooltip>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>

                    {/* Format preview + downloads */}
                    <Box>
                      <Heading size='xs' textTransform='uppercase' color={colorMode === 'light' ? 'gray.600' : 'gray.300'} mb={2}>
                        Format Preview
                      </Heading>
                      <TableContainer overflowY="auto" overflowX="auto" borderWidth="1px" borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'} rounded="md">
                        <Table size='sm' variant='simple'>
                          <Thead bgColor={colorMode === 'light' ? "gray.50" : "gray.800"}>
                            <Tr>
                              <Th overflow='auto'>security_type</Th>
                              <Th overflow='auto'>ticker_name</Th>
                              <Th overflow='auto'>execution_time</Th>
                              <Th overflow='auto'>side</Th>
                              <Th overflow='auto'>expiry</Th>
                              <Th overflow='auto'>strike</Th>
                              <Th overflow='auto'>cost_basis</Th>
                              <Th overflow='auto'>quantity</Th>
                              <Th overflow='auto'>fees</Th>
                            </Tr>
                          </Thead>
                        </Table>
                      </TableContainer>
                      <HStack mt={3} spacing={2} flexWrap="wrap">
                        <Button as={Link} href="./csv/data.csv" download="example_trade_history.csv" size="sm" colorScheme="blue" variant="outline" _hover={{ textDecoration: 'none' }}>
                          ⬇ Download example.csv
                        </Button>
                        <Link href="./csv/accepted_date_formats.csv" download="accepted_date_formats.csv" color='blue.500' fontSize="sm">
                          Accepted date formats →
                        </Link>
                      </HStack>
                    </Box>

                    {/* Tips */}
                    <Box bg={colorMode === 'light' ? 'yellow.50' : 'yellow.900'} borderLeftWidth="4px" borderLeftColor="yellow.400" rounded="md" p={3}>
                      <Heading size='xs' textTransform='uppercase' color={colorMode === 'light' ? 'yellow.800' : 'yellow.200'} mb={2}>
                        Tips
                      </Heading>
                      <UnorderedList spacing={1} fontSize="sm" pl={4}>
                        <ListItem>File must contain <strong>only</strong> your Buy and Sell trade history.</ListItem>
                        <ListItem>All trades must be <strong>closed out</strong> — every Buy needs a matching Sell.</ListItem>
                      </UnorderedList>
                    </Box>
                  </VStack>
                </AlertDialogBody>
                <Stack
                  direction={["column", "row"]}
                  position="relative"
                >
                <VStack spacing={2} align="flex-start" ml={5}>
                  <input
                    type="file"
                    accept=".csv"
                    hidden
                    id="csv-import-upload"
                    onChange={handleFileInputChange}
                  />
                  <HStack spacing={3}>
                    <Button
                      variant="outline"
                      colorScheme="blue"
                      borderRadius="md"
                      size="sm"
                      onClick={() => document.getElementById('csv-import-upload').click()}
                    >
                      Choose File
                    </Button>
                    {selectedFile && <Text fontSize="xs" color="gray.500">{selectedFile.name}</Text>}
                  </HStack>
                  {tradeLoading && <Text color='red.500' fontSize="sm">Loading your Trades...</Text>}
                </VStack>
                <AccountValueSyncToggle
                  isChecked={accountValueImportEnable}
                  onChange={(e) => handleCheckboxChange(e, 'Import')}
                  actionLabel="these imported trades"
                  pl={6}
                  pr={5}
                />
                </Stack>
                {isFreeUser && aiCsvQueriesRemaining !== null && aiCsvQueriesRemaining > 0 && (
                  <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.400'} mt={2} ml={6}>
                    {aiCsvQueriesRemaining}/10 AI queries remaining
                  </Text>
                )}
                {csvQuotaExhausted && (
                  <Box mt={2} ml={6} p={2} bg="orange.100" borderRadius="md">
                    <Text fontSize="xs" color="orange.800" fontWeight="bold">
                      You've used all 10 free AI queries. Upgrade to a paid plan for unlimited access.
                    </Text>
                  </Box>
                )}
                <AlertDialogFooter paddingTop={10}>
                  <ButtonGroup spacing={2}>
                    <Button ref={cancelRef} minWidth='100px' onClick={e => handleCancelImportCsv(e)}>
                      Cancel
                    </Button>
                    <Button
                      colorScheme='gray'
                      minWidth='100px'
                      isDisabled={!selectedFile || tradeLoading || csvFixLoading}
                      onClick={() => handleConfirmImportCsvDirect()}
                    >
                      Default Import
                    </Button>
                    <Button
                      colorScheme='blue'
                      minWidth='100px'
                      isDisabled={!selectedFile || csvFixLoading || csvQuotaExhausted}
                      onClick={e => handleFixCsv(e)}
                      isLoading={tradeLoading || csvFixLoading}
                      loadingText="AI Import"
                    >
                      AI Import
                    </Button>
                  </ButtonGroup>
                </AlertDialogFooter>
              </AlertDialogContent>
              </AlertDialogOverlay>
            </AlertDialog>
            
            {/* CSV Fix Results Dialog */}
            <CsvFixResultsDialog
              isOpen={csvFixDialog}
              cancelRef={btnRef}
              csvFixResult={csvFixResult}
              fixedCsvContent={fixedCsvContent}
              editableCsvData={editableCsvData}
              onEditableCsvChange={setEditableCsvData}
              selectedFile={selectedFile}
              csvFixIsBulkUpdate={csvFixIsBulkUpdate}
              onClose={() => {
                resetCsvFixState();
                setSelectedFile(null);
              }}
              onResetFixState={() => {
                resetCsvFixState();
                setSelectedFile(null);
              }}
              onImport={handleConfirmImportCsvDirect}
              onBulkUpdate={handleConfirmBulkUpdateCsv}
            />
            
            <Button size="sm" variant="outline" colorScheme="gray" borderRadius="md" borderColor={colorMode === 'light' ? 'gray.300' : undefined} onClick={(e) => handleExport(e.target.value)} isLoading={tradeLoading && exportLoading}>
              Export
            </Button>
            <Button size="sm" colorScheme="blue" borderRadius="md" onClick={(e) => handleLogTrade(e.target.value)}>
              + Add Trade
            </Button>
            <Button size="sm" colorScheme="blue" variant="outline" borderRadius="md" isDisabled={selectedRow.length === 0} onClick={e => handleGotoEdit(e)} isLoading={tradeLoading && !deletealertdialog && !importCsvDialog && !exportLoading && editLoading}>
              Edit
            </Button>
            <Button size="sm" colorScheme="red" variant="outline" borderRadius="md" isDisabled={selectedRow.length === 0} onClick={e => handleDeleteButton(e)}>
              Delete
            </Button>
            </HStack>

            {/* Pagination */}
            <TradePagination
              numResults={num_results}
              startOffset={pageStartOffset}
              endOffset={pageEndOffset}
              totalCount={totalCount}
              canGoPrev={backPageEnable}
              canGoNext={nextPageEnable}
              onBackPage={handleBackPage}
              onNextPage={handleNextPage}
              onNumResultsChange={handleChangeNumResults}
            />
            <TradeTable
              hasTrades={hasTrades}
              sortedTrades={sortedTrades}
              selectedRow={selectedRow}
              onRowClick={handleTableRowClick}
              requestSort={requestSort}
              getSortIcon={getSortIcon}
            />
            {deletealertdialog} ? (
              <AlertDialog
              motionPreset='slideInBottom'
              isOpen={isOpen}
              leastDestructiveRef={cancelRef}
              onClose={e => handleCancelDelete(e)}
              isCentered={true}
              closeOnOverlayClick={false}
            >
            {tradeLoading && deletealertdialog?
            <AlertDialogOverlay>
              <AlertDialogContent>
              <Center p={6}>
                <TradingSpinner size="40px" />
              </Center>
              </AlertDialogContent>
            </AlertDialogOverlay>
            :
              <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {trade_ids.length > 1 ? 'Delete Trades' : 'Delete Trade'}
                </AlertDialogHeader>

                <AlertDialogBody>
                  Are you sure? You can't undo this action afterwards.
                  <AccountValueSyncToggle
                    isChecked={accountValueDeleteEnable}
                    onChange={(e) => handleCheckboxChange(e, "Delete")}
                    actionLabel="deleting these trades"
                    pt={3}
                  />
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={e => handleCancelDelete(e)}>
                    Cancel
                  </Button>
                  <Button colorScheme='red' onClick={e => handleConfirmDelete(e)} ml={3}>
                    Delete
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
              </AlertDialogOverlay>
            }
            </AlertDialog>
            ) : (

            )
            </VStack>
            </motion.div>
          }
          </AnimatePresence>
        </Box>
      </Flex>
    ) : (
      <Flex
        flexDirection="column"
        minHeight="100vh"
        backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}
        justifyContent="center"
        alignItems="center"
        pt="20px"
        pb="40px"
      >
        <VStack pt={2} spacing={0}>
          {!bulkUpdateWithCSV ? (
            getEditComponent()
          ) : (
            getBulkUpdateCSVComponent()
          )}
        </VStack>
        
        {/* CSV Fix Results Dialog - Always available */}
        <CsvFixResultsDialog
          isOpen={csvFixDialog}
          cancelRef={btnRef}
          csvFixResult={csvFixResult}
          fixedCsvContent={fixedCsvContent}
          editableCsvData={editableCsvData}
          onEditableCsvChange={setEditableCsvData}
          selectedFile={selectedFile}
          csvFixIsBulkUpdate={csvFixIsBulkUpdate}
          onClose={resetCsvFixState}
          onResetFixState={() => {
            resetCsvFixState();
            setSelectedFile(null);
          }}
          onImport={handleConfirmImportCsvDirect}
          onBulkUpdate={handleConfirmBulkUpdateCsv}
        />
      </Flex>
    ) 
  )
}
